import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TOUR_SEO from '../data/tour-seo.json';
import { CONSENT_EVENT, syncClarityConsent } from '../lib/clarityConsent';

/**
 * Microsoft Clarity loader — free unlimited session recording + heatmaps.
 *
 * Activation:
 *   1. Sign up at https://clarity.microsoft.com (free, unlimited).
 *   2. Create a project for nycroyalcarriage.com → copy the Project ID.
 *   3. Open admin panel → Session Insights → paste the Project ID and save.
 *      (It's stored in site_settings under the key `clarity_project_id`.)
 *
 * The script is only injected when a non-empty Project ID is configured. Admin
 * routes are excluded so internal usage doesn't pollute the recordings. Custom
 * tags expose useful filters in the Clarity dashboard (page type, viewport,
 * referrer host) without leaking PII.
 */
const ClarityLoader = () => {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Skip admin/login — recording our own clicks would muddy the data.
    if (/^\/(admin|login)/.test(location.pathname)) return;

    let cancelled = false;

    const init = async () => {
      try {
        const res = await fetch('/api/site-settings-api?key=clarity_project_id');
        if (!res.ok) return;
        const data = await res.json();
        const projectId = (data.setting?.value || data.value || '').trim();
        if (!projectId || cancelled) return;
        if (window.__clarityLoaded) return;
        window.__clarityLoaded = true;

        // Queue the decision BEFORE the remote script can execute. Project
        // Settings > Setup > Cookies must also be OFF (Consent Mode).
        // https://learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-consent-api-v2
        window.clarity = window.clarity || function () {
          (window.clarity.q = window.clarity.q || []).push(arguments);
        };
        syncClarityConsent();

        // Standard Clarity bootstrap snippet (taken verbatim from clarity.microsoft.com).
        (function (c, l, a, r, i, t, y) {
          c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
          t = l.createElement(r);
          t.async = 1;
          t.src = 'https://www.clarity.ms/tag/' + i;
          y = l.getElementsByTagName(r)[0];
          y.parentNode.insertBefore(t, y);
        })(window, document, 'clarity', 'script', projectId);

        // Custom tags — visible as filters in Clarity dashboard.
        // Page type lets us slice by tours/booking/blog/etc; viewport class lets
        // us answer "do mobile users abandon checkout more than desktop?".
        window.clarity('set', 'pageType', pageTypeFor(location.pathname));
        window.clarity('set', 'viewport', window.innerWidth < 768 ? 'mobile' : 'desktop');
        if (document.referrer) {
          try {
            window.clarity('set', 'referrerHost', new URL(document.referrer).hostname);
          } catch (_) {}
        }
      } catch (e) {
        // Stay silent — Clarity failing should never break the site.
      }
    };

    init();
    return () => { cancelled = true; };
  }, [location.pathname]);

  useEffect(() => {
    const storageChanged = event => { if (!event.key || event.key === 'cookie_consent') syncClarityConsent(); };
    window.addEventListener(CONSENT_EVENT, syncClarityConsent);
    window.addEventListener('storage', storageChanged);
    return () => {
      window.removeEventListener(CONSENT_EVENT, syncClarityConsent);
      window.removeEventListener('storage', storageChanged);
    };
  }, []);

  // Update pageType when route changes (Clarity treats single SPAs as one session).
  useEffect(() => {
    if (window.clarity) {
      window.clarity('set', 'pageType', pageTypeFor(location.pathname));
    }
  }, [location.pathname]);

  return null;
};

export function pageTypeFor(path) {
  const pathname = path.replace(/\/+$/, '') || '/';
  if (pathname === '/') return 'home';
  if (pathname === '/book' || pathname.startsWith('/book/')) return 'booking';
  if (Object.prototype.hasOwnProperty.call(TOUR_SEO, pathname.slice(1))) return 'tour-detail';
  if (pathname.startsWith('/tours/')) return 'tour-detail';
  if (pathname === '/tours') return 'tours-list';
  if (pathname.startsWith('/blog')) return 'blog';
  if (pathname.startsWith('/events')) return 'events';
  if (pathname.startsWith('/proposals')) return 'proposals';
  if (pathname.startsWith('/booking-success')) return 'booking-success';
  if (pathname.startsWith('/areas/')) return 'area';
  if (pathname === '/contact') return 'contact';
  if (pathname === '/about') return 'about';
  if (pathname === '/faq') return 'faq';
  if (pathname === '/gallery') return 'gallery';
  return 'other';
}

export default ClarityLoader;
